import React, { useState } from 'react';
import { 
  ChevronLeft, ChevronDown, ChevronRight, Truck, Package, CheckCircle, 
  Clock, CreditCard, DollarSign, AlertCircle
} from 'lucide-react';

const DELIVERY_STATUS_CONFIG = {
  '待送货': { color: 'bg-slate-500', bgColor: 'bg-slate-50', icon: Clock },
  '已到场': { color: 'bg-blue-500', bgColor: 'bg-blue-50', icon: Truck },
  '已入库': { color: 'bg-purple-500', bgColor: 'bg-purple-50', icon: Package },
  '已结算': { color: 'bg-amber-500', bgColor: 'bg-amber-50', icon: DollarSign },
  '已付款': { color: 'bg-green-500', bgColor: 'bg-green-50', icon: CreditCard },
};

const MOCK_ORDER = {
  orderNo: 'ORD20260218001',
  status: '送货中',
  baseName: '马钢诚兴马鞍山基地',
  material: '重型废钢 (HMS1)',
  estimatedWeight: 50,
  progress: { total: 5, waiting: 1, inProgress: 2, completed: 2 },
  deliveries: [
    { id: 'D001', deliveryNo: 'DEL20260218001', status: '已付款', weight: 10.2, amount: 27030, vehicleNo: '皖E12345', driverName: '张师傅' },
    { id: 'D002', deliveryNo: 'DEL20260218002', status: '已结算', weight: 12.3, amount: 32595, vehicleNo: '皖E23456', driverName: '李师傅' },
    { id: 'D003', deliveryNo: 'DEL20260218003', status: '已入库', weight: 5.0, vehicleNo: '皖E34567', driverName: '王师傅' },
    { id: 'D004', deliveryNo: 'DEL20260218004', status: '已到场', vehicleNo: '皖E45678', driverName: '赵师傅' },
    { id: 'D005', deliveryNo: 'DEL20260218005', status: '待送货' },
  ]
};

const GROUP_CONFIG = {
  '待处理': {
    statuses: ['待送货'],
    icon: AlertCircle,
    color: 'text-orange-500',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
  },
  '进行中': {
    statuses: ['已到场', '已入库'],
    icon: Truck,
    color: 'text-blue-500',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
  },
  '已完成': {
    statuses: ['已结算', '已付款'],
    icon: CheckCircle,
    color: 'text-green-500',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
  },
};

const DeliveryGroup = ({ title, deliveries, groupConfig }) => {
  const [expanded, setExpanded] = useState(true);
  const Icon = groupConfig.icon;
  
  if (deliveries.length === 0) return null;
  
  return (
    <div className="mb-4">
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full flex items-center justify-between p-3 rounded-xl ${groupConfig.bgColor} border ${groupConfig.borderColor}`}
      >
        <div className="flex items-center gap-2">
          <Icon size={18} className={groupConfig.color} />
          <span className="font-bold text-slate-800">{title}</span>
          <span className={`text-sm font-bold ${groupConfig.color}`}>({deliveries.length})</span>
        </div>
        <ChevronDown 
          size={18} 
          className={`text-slate-400 transition-transform ${expanded ? '' : '-rotate-90'}`} 
        />
      </button>
      
      {expanded && (
        <div className="mt-2 space-y-2">
          {deliveries.map((delivery) => {
            const config = DELIVERY_STATUS_CONFIG[delivery.status];
            const StatusIcon = config.icon;
            
            return (
              <div 
                key={delivery.id}
                className="bg-white rounded-lg border border-slate-100 p-3 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg ${config.color} flex items-center justify-center`}>
                    <StatusIcon size={16} className="text-white" />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-slate-800">{delivery.deliveryNo}</div>
                    <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                      {delivery.weight && <span>{delivery.weight}吨</span>}
                      {delivery.amount && <span className="text-red-600 font-bold">¥{delivery.amount.toLocaleString()}</span>}
                      {delivery.driverName && <span>· {delivery.driverName}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-1 rounded-full font-bold ${config.color} text-white`}>
                    {delivery.status}
                  </span>
                  <ChevronRight size={16} className="text-slate-300" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default function OrderDetailPrototype2({ onBack }) {
  const order = MOCK_ORDER;
  
  const groupedDeliveries = {
    '待处理': order.deliveries.filter(d => GROUP_CONFIG['待处理'].statuses.includes(d.status)),
    '进行中': order.deliveries.filter(d => GROUP_CONFIG['进行中'].statuses.includes(d.status)),
    '已完成': order.deliveries.filter(d => GROUP_CONFIG['已完成'].statuses.includes(d.status)),
  };
  
  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="flex items-center justify-between px-4 py-3">
          <button onClick={onBack} className="flex items-center gap-1 text-slate-600">
            <ChevronLeft size={20} />
            <span>返回</span>
          </button>
          <h1 className="font-bold text-slate-800">订单详情</h1>
          <div className="w-16" />
        </div>
      </div>
      
      <div className="p-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100 mb-4">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="font-bold text-slate-800">{order.orderNo}</div>
              <div className="text-xs text-slate-500 mt-1">{order.baseName}</div>
            </div>
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-blue-500 text-white">
              {order.status}
            </span>
          </div>
          
          <div className="grid grid-cols-3 gap-3 pt-3 border-t border-slate-100">
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-500">{order.progress.waiting}</div>
              <div className="text-xs text-slate-500 mt-1">待处理</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-500">{order.progress.inProgress}</div>
              <div className="text-xs text-slate-500 mt-1">进行中</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-500">{order.progress.completed}</div>
              <div className="text-xs text-slate-500 mt-1">已完成</div>
            </div>
          </div>
        </div>
        
        {Object.entries(GROUP_CONFIG).map(([title, config]) => (
          <DeliveryGroup
            key={title}
            title={title}
            deliveries={groupedDeliveries[title]}
            groupConfig={config}
          />
        ))}
      </div>
      
      <div className="fixed bottom-16 left-0 right-0 bg-white border-t border-slate-200 p-4">
        <button className="w-full bg-blue-500 text-white py-3 rounded-xl font-bold">
          新增送货单
        </button>
      </div>
    </div>
  );
}